import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiSearch, FiX, FiClock, FiTrendingUp } from 'react-icons/fi'
import { gigApi } from '@/services/api/gigs'
import { Gig } from '@/types/gig'
import LoadingSpinner from './LoadingSpinner'
import { CurrencyDisplay } from '@/components/currency'

interface SearchModalProps {
    isOpen: boolean
    onClose: () => void
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<Gig[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [recentSearches, setRecentSearches] = useState<string[]>([])
    const navigate = useNavigate()
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Helper to get category name whether it's string or object
    const getCategoryName = (category: any) => {
        if (!category) return 'Unknown'
        if (typeof category === 'string') {
            return category.replace('-', ' ')
        }
        return (category.name || category.slug || 'Unknown').replace('-', ' ')
    }

    // Load recent searches from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('recent-searches')
        if (saved) {
            try {
                setRecentSearches(JSON.parse(saved))
            } catch {
                setRecentSearches([])
            }
        }
    }, [])

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isOpen])

    // Debounced search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([])
            return
        }

        const timeoutId = setTimeout(async () => {
            setIsLoading(true)
            try {
                const response = await gigApi.searchGigs({
                    query: searchQuery,
                    limit: 6
                })
                // @ts-expect-error - Intentional nested data structure for API response
                setSearchResults(response.data.data.gigs || [])
            } catch (error) {
                console.error('Search failed:', error)
                setSearchResults([])
            } finally {
                setIsLoading(false)
            }
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchQuery])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && searchQuery.trim()) {
            handleSearch()
        }
    }

    const handleSearch = () => {
        if (!searchQuery.trim()) return

        // Save to recent searches
        const updatedRecentSearches = [
            searchQuery,
            ...recentSearches.filter(s => s !== searchQuery)
        ].slice(0, 5)

        setRecentSearches(updatedRecentSearches)
        localStorage.setItem('recent-searches', JSON.stringify(updatedRecentSearches))

        // Navigate to search results
        navigate(`/gigs?search=${encodeURIComponent(searchQuery)}`)
        onClose()
    }

    const handleGigClick = (gigId: string) => {
        navigate(`/gigs/${gigId}`)
        onClose()
    }

    const handleRecentSearchClick = (query: string) => {
        setSearchQuery(query)
        navigate(`/gigs?search=${encodeURIComponent(query)}`)
        onClose()
    }

    const clearRecentSearches = () => {
        setRecentSearches([])
        localStorage.removeItem('recent-searches')
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                <div className="flex items-center space-x-4">
                    <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg font-inter"
                            placeholder="Search for gigs, skills, or services..."
                        />
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <FiX className="h-6 w-6" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <LoadingSpinner />
                    </div>
                ) : searchQuery ? (
                    searchResults && searchResults.length > 0 ? (
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                                <FiSearch className="h-4 w-4 mr-2" />
                                Search Results ({searchResults.length})
                            </h3>
                            <div className="space-y-3">
                                {searchResults.map((gig) => (
                                    <div
                                        key={gig._id}
                                        onClick={() => handleGigClick(gig._id)}
                                        className="block p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                    >
                                        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                                            {gig.title}
                                        </h4>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                                            {gig.description}
                                        </p>
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 capitalize">
                                                {getCategoryName(gig.category)}
                                            </span>
                                            <div className="flex items-center gap-1 font-medium">
                                                <CurrencyDisplay
                                                    amount={gig.payment.amount}
                                                    currency={gig.payment.currency}
                                                    compact={true}
                                                    variant="success"
                                                    className="text-green-600 dark:text-green-400"
                                                    size="sm"
                                                />
                                                {gig.payment.type === 'hourly' && <span>/hr</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {searchResults && searchResults.length === 6 && (
                                <button
                                    onClick={handleSearch}
                                    className="w-full mt-4 py-3 text-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                                >
                                    View all results
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <FiSearch className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                                No results found
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Try searching with different keywords
                            </p>
                        </div>
                    )
                ) : (
                    <div>
                        {/* Recent Searches */}
                        {recentSearches.length > 0 && (
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                                        <FiClock className="h-4 w-4 mr-2" />
                                        Recent Searches
                                    </h3>
                                    <button
                                        onClick={clearRecentSearches}
                                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                    >
                                        Clear all
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {recentSearches.map((query, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleRecentSearchClick(query)}
                                            className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                                        >
                                            <FiClock className="h-4 w-4 text-gray-400 mr-3" />
                                            <span className="text-sm text-gray-900 dark:text-white">{query}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Popular Searches */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                                <FiTrendingUp className="h-4 w-4 mr-2" />
                                Popular Searches
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    'Delivery',
                                    'Cleaning',
                                    'Tutoring',
                                    'Photography',
                                    'Writing',
                                    'Design',
                                    'Tech Support',
                                    'Moving'
                                ].map((term) => (
                                    <button
                                        key={term}
                                        onClick={() => handleRecentSearchClick(term)}
                                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                                    >
                                        <span className="text-sm text-gray-900 dark:text-white">{term}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchModal